import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Download,
  Monitor,
  Apple,
  Terminal,
  Shield,
  CheckCircle,
  Copy,
  RefreshCw,
  Cpu,
  HardDrive,
  Wifi,
  Lock,
  Zap,
  Globe,
  FileSearch,
  Activity,
  Clock,
  Server,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Eye,
  EyeOff,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { getAgentInfo, getAgentDevices } from '../services/api';
import { toast } from 'sonner';

const PlatformIcon = ({ platform, className }) => {
  switch (platform) {
    case 'windows':
      return <Monitor className={className} />;
    case 'macos':
      return <Apple className={className} />;
    case 'linux':
      return <Terminal className={className} />;
    default:
      return <Monitor className={className} />;
  }
};

const platformColors = {
  windows: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
  macos: 'bg-slate-500/10 text-slate-300 border-slate-500/20',
  linux: 'bg-orange-500/10 text-orange-500 border-orange-500/20',
};

const FeatureItem = ({ icon: Icon, title, description }) => (
  <div className="flex items-start gap-3 p-4 rounded-lg bg-slate-800/50">
    <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0">
      <Icon className="w-5 h-5 text-blue-500" />
    </div>
    <div>
      <h4 className="font-medium text-white">{title}</h4>
      <p className="text-sm text-slate-400 mt-1">{description}</p>
    </div>
  </div>
);

export default function AgentDownload() {
  const [agentInfo, setAgentInfo] = useState(null);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlatform, setSelectedPlatform] = useState('macos');
  const [copiedKey, setCopiedKey] = useState(false);
  const [showKey, setShowKey] = useState(false);

  // Only show macOS platform
  const enabledPlatforms = ['macos'];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [infoRes, devicesRes] = await Promise.all([
        getAgentInfo(),
        getAgentDevices(),
      ]);
      setAgentInfo(infoRes.data);
      setDevices(devicesRes.data);
    } catch (error) {
      console.error('Failed to fetch agent info:', error);
      toast.error('Failed to load agent information');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyKey = () => {
    if (agentInfo?.deployment_key) {
      navigator.clipboard.writeText(agentInfo.deployment_key);
      setCopiedKey(true);
      toast.success('Deployment key copied to clipboard');
      setTimeout(() => setCopiedKey(false), 2000);
    }
  };

  const handleDownload = (platform) => {
    toast.info(`Download for ${platform} would start here. This is a demo.`);
  };

  const selectedPlatformInfo = agentInfo?.platforms?.find(p => p.os === selectedPlatform);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="agent-download-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-3xl text-white flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
              <Download className="w-5 h-5 text-emerald-500" />
            </div>
            Endpoint Agent
          </h1>
          <p className="text-slate-400 mt-1">Download and deploy the Clear Defense AI security agent</p>
        </div>
        <div className="flex items-center gap-3">
          <Badge className="bg-emerald-500/10 text-emerald-500 border-emerald-500/20 px-3 py-1">
            Version {agentInfo?.version}
          </Badge>
          <Button variant="outline" onClick={fetchData} className="border-slate-700">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Deployment Key Card */}
      <Card className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 border-blue-500/20">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Lock className="w-5 h-5 text-blue-500" />
                <h3 className="font-heading font-semibold text-lg text-white">Your Deployment Key</h3>
              </div>
              <p className="text-sm text-slate-400">
                Use this key during agent installation to connect to your organization: <strong className="text-white">{agentInfo?.tenant_name}</strong>
              </p>
            </div>
            <div className="flex items-center gap-3">
              <code className="px-4 py-2 rounded-lg bg-slate-900 text-emerald-400 font-mono text-lg tracking-wider min-w-[200px]">
                {showKey ? agentInfo?.deployment_key : '••••••••••••••••'}
              </code>
              <Button 
                onClick={() => setShowKey(!showKey)}
                variant="outline"
                className="border-slate-700"
                title={showKey ? 'Hide key' : 'Show key'}
              >
                {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </Button>
              <Button 
                onClick={handleCopyKey}
                variant="outline"
                className={copiedKey ? 'border-emerald-500 text-emerald-500' : 'border-slate-700'}
                title="Copy to clipboard"
              >
                {copiedKey ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="download" className="w-full">
        <TabsList className="bg-slate-900 border border-slate-800">
          <TabsTrigger value="download" className="data-[state=active]:bg-slate-800">
            <Download className="w-4 h-4 mr-2" />
            Download
          </TabsTrigger>
          <TabsTrigger value="browser" className="data-[state=active]:bg-slate-800">
            <Globe className="w-4 h-4 mr-2" />
            Browser Extension
          </TabsTrigger>
          <TabsTrigger value="devices" className="data-[state=active]:bg-slate-800">
            <Monitor className="w-4 h-4 mr-2" />
            Deployed Devices ({devices?.total || 0})
          </TabsTrigger>
          <TabsTrigger value="features" className="data-[state=active]:bg-slate-800">
            <Zap className="w-4 h-4 mr-2" />
            Features
          </TabsTrigger>
        </TabsList>

        {/* Browser Extension Tab */}
        <TabsContent value="browser" className="space-y-6 mt-6">
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-blue-500/20 to-purple-500/20 flex items-center justify-center">
                  <Shield className="w-8 h-8 text-blue-500" />
                </div>
                <div>
                  <CardTitle className="text-white text-xl">Clear Defense AI Browser Extension</CardTitle>
                  <CardDescription className="text-slate-400">
                    Real-time web protection for Chrome and Firefox
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Features Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3 p-4 rounded-lg bg-slate-800/50">
                  <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center flex-shrink-0">
                    <Shield className="w-5 h-5 text-red-500" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">Real-time URL Protection</h4>
                    <p className="text-sm text-slate-400">Automatically blocks malicious websites before they can harm you</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 rounded-lg bg-slate-800/50">
                  <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center flex-shrink-0">
                    <FileSearch className="w-5 h-5 text-yellow-500" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">Phishing Detection</h4>
                    <p className="text-sm text-slate-400">AI-powered detection of phishing and credential theft attempts</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 rounded-lg bg-slate-800/50">
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-emerald-500" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">Safe Browsing Indicator</h4>
                    <p className="text-sm text-slate-400">Visual status showing your protection level on every page</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 rounded-lg bg-slate-800/50">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                    <Activity className="w-5 h-5 text-blue-500" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">Manual URL Check</h4>
                    <p className="text-sm text-slate-400">Right-click any link to scan it before visiting</p>
                  </div>
                </div>
              </div>

              {/* Download Buttons */}
              <div className="border-t border-slate-800 pt-6">
                <h4 className="text-white font-medium mb-4">Install the Extension</h4>
                <div className="flex flex-wrap gap-4">
                  <Button 
                    className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800"
                    onClick={() => toast.info('Chrome extension download will be available soon. Contact support for early access.')}
                  >
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 0C8.21 0 4.831 1.757 2.632 4.501l3.953 6.848A5.454 5.454 0 0 1 12 6.545h10.691A12 12 0 0 0 12 0zM1.931 5.47A11.943 11.943 0 0 0 0 12c0 6.012 4.42 10.991 10.189 11.864l3.953-6.847a5.45 5.45 0 0 1-6.865-2.29zm13.342 2.166a5.446 5.446 0 0 1 1.45 7.09l.002.001h-.002l-3.952 6.848a12.014 12.014 0 0 0 9.229-9.006zM12 16.364a4.364 4.364 0 1 1 0-8.728 4.364 4.364 0 0 1 0 8.728z"/>
                    </svg>
                    Chrome Web Store
                  </Button>
                  <Button 
                    variant="outline"
                    className="border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
                    onClick={() => toast.info('Firefox extension download will be available soon. Contact support for early access.')}
                  >
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12.001 0C5.373 0 0 5.373 0 12s5.373 12 12.001 12C18.628 24 24 18.627 24 12S18.628 0 12.001 0zm6.934 6.416c.256.624.392 1.296.392 2.008 0 1.488-.568 2.84-1.496 3.856l.008.008c-.24-.408-.64-.872-1.256-1.192-.6-.312-1.008-.488-1.536-.72-.128-.056-.272-.128-.424-.2.152-.024.296-.04.432-.04.576 0 .984.128 1.456.36.472.24.696.4.88.56.016-.176.024-.36.024-.544 0-.968-.304-1.872-.824-2.616-.144.056-.336.12-.584.168a7.72 7.72 0 0 1-.808.096c-.248.016-.504.024-.768.024-1.048 0-2.04-.136-2.944-.392a8.61 8.61 0 0 1-.76-.24 3.865 3.865 0 0 1-.28-.104c.568-.728 1.432-1.296 2.488-1.608a6.19 6.19 0 0 1 1.688-.232c.408 0 .808.032 1.192.096.384.064.752.16 1.1.288a5.7 5.7 0 0 1 1.52.824z"/>
                    </svg>
                    Firefox Add-ons
                  </Button>
                </div>
              </div>

              {/* Installation Instructions */}
              <div className="border-t border-slate-800 pt-6">
                <h4 className="text-white font-medium mb-4">Manual Installation (Developer Mode)</h4>
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="chrome" className="border-slate-800">
                    <AccordionTrigger className="text-slate-300 hover:text-white">
                      Chrome Installation
                    </AccordionTrigger>
                    <AccordionContent className="text-slate-400">
                      <ol className="list-decimal list-inside space-y-2">
                        <li>Download the extension files from your admin panel</li>
                        <li>Open Chrome and navigate to <code className="text-emerald-400">chrome://extensions/</code></li>
                        <li>Enable &quot;Developer mode&quot; (toggle in top-right corner)</li>
                        <li>Click &quot;Load unpacked&quot; and select the extension folder</li>
                        <li>Sign in with your Clear Defense AI credentials</li>
                      </ol>
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="firefox" className="border-slate-800">
                    <AccordionTrigger className="text-slate-300 hover:text-white">
                      Firefox Installation
                    </AccordionTrigger>
                    <AccordionContent className="text-slate-400">
                      <ol className="list-decimal list-inside space-y-2">
                        <li>Download the extension files from your admin panel</li>
                        <li>Open Firefox and navigate to <code className="text-emerald-400">about:debugging#/runtime/this-firefox</code></li>
                        <li>Click &quot;Load Temporary Add-on&quot;</li>
                        <li>Select the manifest.json file from the extension folder</li>
                        <li>Sign in with your Clear Defense AI credentials</li>
                      </ol>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>

              {/* Extension Stats */}
              <div className="border-t border-slate-800 pt-6">
                <h4 className="text-white font-medium mb-4">Extension Protection Stats</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-4 rounded-lg bg-slate-800/50">
                    <div className="text-3xl font-bold text-red-400">0</div>
                    <div className="text-xs text-slate-500 mt-1">Threats Blocked</div>
                  </div>
                  <div className="text-center p-4 rounded-lg bg-slate-800/50">
                    <div className="text-3xl font-bold text-blue-400">0</div>
                    <div className="text-xs text-slate-500 mt-1">Pages Scanned</div>
                  </div>
                  <div className="text-center p-4 rounded-lg bg-slate-800/50">
                    <div className="text-3xl font-bold text-yellow-400">0</div>
                    <div className="text-xs text-slate-500 mt-1">Phishing Detected</div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Download Tab */}
        <TabsContent value="download" className="space-y-6 mt-6">
          {/* Platform Selection */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {agentInfo?.platforms?.filter(p => enabledPlatforms.includes(p.os)).map((platform) => (
              <motion.div
                key={platform.os}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Card 
                  className={`cursor-pointer transition-all ${
                    selectedPlatform === platform.os 
                      ? 'bg-slate-800 border-blue-500' 
                      : 'bg-slate-900 border-slate-800 hover:border-slate-700'
                  }`}
                  onClick={() => setSelectedPlatform(platform.os)}
                >
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${platformColors[platform.os]}`}>
                        <PlatformIcon platform={platform.os} className="w-7 h-7" />
                      </div>
                      <div>
                        <h3 className="font-heading font-semibold text-lg text-white">{platform.name}</h3>
                        <p className="text-sm text-slate-400">{platform.file_size}</p>
                      </div>
                    </div>
                    {selectedPlatform === platform.os && (
                      <div className="mt-4 pt-4 border-t border-slate-700">
                        <p className="text-xs text-slate-400 mb-2">Supported architectures:</p>
                        <div className="flex flex-wrap gap-1">
                          {(platform.architecture || platform.versions || []).map((v, i) => (
                            <Badge key={i} variant="outline" className="text-xs border-slate-700 text-slate-400">
                              {v}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            ))}
            
            {/* Disabled platforms */}
            {agentInfo?.platforms?.filter(p => !enabledPlatforms.includes(p.os)).map((platform) => (
              <motion.div key={platform.os}>
                <Card className="bg-slate-900/50 border-slate-800 opacity-50 cursor-not-allowed">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <div className={`w-14 h-14 rounded-xl flex items-center justify-center bg-slate-800`}>
                        <PlatformIcon platform={platform.os} className="w-7 h-7 text-slate-600" />
                      </div>
                      <div>
                        <h3 className="font-heading font-semibold text-lg text-slate-500">{platform.name}</h3>
                        <p className="text-sm text-slate-600">Coming Soon</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Selected Platform Details */}
          {selectedPlatformInfo && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Download Card */}
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader>
                  <CardTitle className="text-lg font-heading text-white flex items-center gap-2">
                    <PlatformIcon platform={selectedPlatform} className="w-5 h-5" />
                    {selectedPlatformInfo.name} Agent
                  </CardTitle>
                  <CardDescription className="text-slate-400">
                    Version {agentInfo?.version} • Released {agentInfo?.release_date}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-4 rounded-lg bg-slate-800/50 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-400">File Name</span>
                      <span className="text-sm text-white font-mono">{selectedPlatformInfo.file_name}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-400">File Size</span>
                      <span className="text-sm text-white">{selectedPlatformInfo.file_size}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-400">Architecture</span>
                      <span className="text-sm text-white">{selectedPlatformInfo.architecture.join(', ')}</span>
                    </div>
                  </div>

                  <Button 
                    onClick={() => handleDownload(selectedPlatform)}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    size="lg"
                  >
                    <Download className="w-5 h-5 mr-2" />
                    Download for {selectedPlatformInfo.name}
                  </Button>

                  <p className="text-xs text-slate-500 text-center">
                    By downloading, you agree to the Clear Defense AI Terms of Service
                  </p>
                </CardContent>
              </Card>

              {/* Installation Steps */}
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader>
                  <CardTitle className="text-lg font-heading text-white">Installation Steps</CardTitle>
                </CardHeader>
                <CardContent>
                  <ol className="space-y-3">
                    {agentInfo?.installation_steps?.[selectedPlatform]?.map((step, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-500/20 text-blue-500 flex items-center justify-center text-sm font-medium">
                          {index + 1}
                        </span>
                        <span className="text-slate-300 text-sm pt-0.5">{step}</span>
                      </li>
                    ))}
                  </ol>
                </CardContent>
              </Card>
            </div>
          )}

          {/* System Requirements */}
          {selectedPlatformInfo && (
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-lg font-heading text-white">System Requirements</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {selectedPlatformInfo.requirements.map((req, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 rounded-lg bg-slate-800/50">
                      <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                      <span className="text-sm text-slate-300">{req}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Deployed Devices Tab */}
        <TabsContent value="devices" className="space-y-6 mt-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                    <Monitor className="w-5 h-5 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-heading font-bold text-white">{devices?.total || 0}</p>
                    <p className="text-xs text-slate-500">Total Devices</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-emerald-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-heading font-bold text-white">{devices?.active || 0}</p>
                    <p className="text-xs text-slate-500">Active</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-slate-500/10 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-slate-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-heading font-bold text-white">
                      {(devices?.total || 0) - (devices?.active || 0)}
                    </p>
                    <p className="text-xs text-slate-500">Inactive</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-purple-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-heading font-bold text-white">
                      {devices?.devices?.reduce((acc, d) => acc + (d.threats_blocked || 0), 0) || 0}
                    </p>
                    <p className="text-xs text-slate-500">Threats Blocked</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-800 hover:bg-transparent">
                    <TableHead className="text-slate-400">Device</TableHead>
                    <TableHead className="text-slate-400">OS</TableHead>
                    <TableHead className="text-slate-400">Agent Version</TableHead>
                    <TableHead className="text-slate-400">Status</TableHead>
                    <TableHead className="text-slate-400">Last Sync</TableHead>
                    <TableHead className="text-slate-400">Threats Blocked</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {devices?.devices?.map((device) => (
                    <TableRow key={device.id} className="border-slate-800 hover:bg-slate-800/50">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center">
                            <Monitor className="w-4 h-4 text-slate-400" />
                          </div>
                          <div>
                            <p className="font-medium text-white">{device.hostname}</p>
                            <p className="text-xs text-slate-500">{device.id.slice(0, 8)}...</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-slate-300">{device.os_type} {device.os_version}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-slate-300">{device.agent_version}</span>
                      </TableCell>
                      <TableCell>
                        <Badge className={
                          device.agent_status === 'active' 
                            ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20'
                            : 'bg-slate-500/10 text-slate-400 border-slate-500/20'
                        }>
                          {device.agent_status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-slate-400 text-sm">
                          {new Date(device.last_sync).toLocaleString()}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-white font-medium">{device.threats_blocked}</span>
                      </TableCell>
                    </TableRow>
                  ))}
                  {(!devices?.devices || devices.devices.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                        No devices with agent installed yet. Download and install the agent to get started.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Features Tab */}
        <TabsContent value="features" className="space-y-6 mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FeatureItem
              icon={Globe}
              title="Real-time DNS Filtering"
              description="Block malicious domains before they can connect. Our AI-powered DNS filter protects against phishing, malware, and command-and-control servers."
            />
            <FeatureItem
              icon={FileSearch}
              title="File Download Scanning"
              description="Every file download is scanned in our cloud sandbox. Suspicious files are quarantined before they can execute on the endpoint."
            />
            <FeatureItem
              icon={Activity}
              title="Process Monitoring"
              description="Continuous monitoring of running processes detects anomalous behavior, privilege escalation, and lateral movement attempts."
            />
            <FeatureItem
              icon={RefreshCw}
              title="Automatic Updates"
              description="The agent automatically updates itself and syncs the latest threat intelligence and policies without user intervention."
            />
            <FeatureItem
              icon={Cpu}
              title="Low Resource Usage"
              description="Optimized for performance with less than 2% CPU usage and under 100MB RAM footprint. Your users won't notice it's running."
            />
            <FeatureItem
              icon={Wifi}
              title="Offline Protection"
              description="Cached threat intelligence ensures protection even when devices are offline or have limited connectivity."
            />
          </div>

          <Card className="bg-slate-900 border-slate-800">
            <CardHeader>
              <CardTitle className="text-lg font-heading text-white">Frequently Asked Questions</CardTitle>
            </CardHeader>
            <CardContent>
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="item-1" className="border-slate-800">
                  <AccordionTrigger className="text-white hover:text-white">
                    How do I uninstall the agent?
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-400">
                    On Windows, use Add/Remove Programs. On macOS, run the uninstaller in /Applications/Clear Defense AI. 
                    On Linux, use your package manager (apt remove or yum remove).
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="item-2" className="border-slate-800">
                  <AccordionTrigger className="text-white hover:text-white">
                    Does the agent work with other security software?
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-400">
                    Yes, Clear Defense AI is designed to work alongside traditional antivirus solutions. 
                    It focuses on network-level protection and behavior analysis rather than signature-based detection.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="item-3" className="border-slate-800">
                  <AccordionTrigger className="text-white hover:text-white">
                    How often does the agent sync with the cloud?
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-400">
                    The agent syncs every 5 minutes for policy updates and threat intelligence. 
                    Real-time events like threats and blocks are reported immediately.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="item-4" className="border-slate-800">
                  <AccordionTrigger className="text-white hover:text-white">
                    Can users disable or bypass the agent?
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-400">
                    The agent includes tamper protection that prevents unauthorized modification or termination. 
                    Only administrators with the proper credentials can disable protection.
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
